import type { Persona } from './personas';
import type { VersionKey } from './versions';

export type UiLanguage = 'en' | 'zh-TW' | 'fr-CA' | 'es-MX';
export type InfoPageKey = 'compliance' | 'technical' | 'pricing' | 'model';

export function resolveUiLanguage(language?: string | null): UiLanguage {
  const value = (language ?? '').toLowerCase();
  if (value.startsWith('zh')) return 'zh-TW';
  if (value.startsWith('fr')) return 'fr-CA';
  if (value.startsWith('es')) return 'es-MX';
  return 'en';
}

export const START_COPY: Record<UiLanguage, {
  backHome: string;
  kicker: string;
  title: string;
  subtitle: string;
  versionSelectorAria: string;
  versionState: { active: string; inactive: string; badge: string };
  versions: Record<VersionKey, { name: string; promise: string; sampleConcern: string }>;
  concern: { title: string; placeholder: string };
  prompts: Array<{ id: string; tag: string; label: string; text: string }>;
  reader: { aria: string; kicker: string; title: string };
  personas: Record<Persona, { name: string; role: string; blurb: string }>;
  auto: { title: string; default: string; recommended: string };
  consent: string;
  actions: { submitting: string; startWith: string };
  errors: { api: string; network: string };
  transition: { kicker: string; title: string };
}> = {
  'zh-TW': {
    backHome: '回首頁',
    kicker: 'Tarot reading room',
    title: '想問什麼？先把問題交給牌桌。',
    subtitle: '選一種解讀氛圍，留下一句心事，再選一位占卜師。下一步會直接進入選牌。',
    versionSelectorAria: '選擇解讀版本',
    versionState: { active: '目前選擇', inactive: '選擇版本', badge: '開始試玩' },
    versions: {
      healing: {
        name: '療癒陪伴版',
        promise: '給情感、關係與心事傾訴使用者，一進來就像被溫柔接住。',
        sampleConcern: '我最近對感情很迷惘，不知道該不該繼續。',
      },
      ritual: {
        name: '神秘儀式版',
        promise: '強化沉浸感、付費感與占卜師權威，像進入一場深夜讀牌。',
        sampleConcern: '我一直覺得某段關係還沒結束，但我不知道該不該回頭。',
      },
      clarity: {
        name: '理性分析版',
        promise: '把塔羅變成清楚的決策輔助，適合不想太迷信但想被點醒的人。',
        sampleConcern: '我卡在一個重要決定裡，理智知道答案，情緒卻一直拉住我。',
      },
    },
    concern: {
      title: '先給占卜師一句心事',
      placeholder: '例如：我最近對感情很迷惘，不知道該不該繼續。',
    },
    prompts: [
      {
        id: 'relationship',
        tag: '關係',
        label: '關係分岔',
        text: '我最近對一段關係很迷惘，不知道該繼續靠近，還是慢慢放手。',
      },
      {
        id: 'career',
        tag: '職涯',
        label: '職涯抉擇',
        text: '我正在考慮工作或合作上的轉換，想知道現在最需要看清楚的是什麼。',
      },
      {
        id: 'intuition',
        tag: '直覺',
        label: '內在訊號',
        text: '我最近一直有一種說不清的直覺，想知道這個感覺在提醒我什麼。',
      },
    ],
    reader: { aria: '選擇占卜師', kicker: 'Reader', title: '選一位占卜師' },
    personas: {
      luna: {
        name: 'Luna',
        role: '療癒陪伴',
        blurb: '適合感情、關係、心事傾訴；語氣溫柔，先接住情緒。',
      },
      orion: {
        name: 'Orion',
        role: '神秘儀式',
        blurb: '適合命運感、重大轉折、想要儀式感與占卜重量的問題。',
      },
      sage: {
        name: 'Sage',
        role: '理性分析',
        blurb: '適合職涯、選擇、決策卡關；會把牌義整理成清楚脈絡。',
      },
    },
    auto: {
      title: '讓系統推薦',
      default: '讀取你輸入的心事，像抽籤一樣幫你配一位。',
      recommended: '已推薦 {{name}}。',
    },
    consent: '我了解這是 AI 輔助塔羅服務，不是醫療、法律或心理治療建議。',
    actions: { submitting: '建立占卜中...', startWith: '用 {{name}} 開始選牌' },
    errors: { api: 'API {{status}} 沒有成功。請稍後重試，或先確認線上後台已部署。', network: '網路連線沒有成功。請稍後重試，或先確認線上後台已部署。' },
    transition: { kicker: 'Transition', title: '正在打開牌桌' },
  },
  en: {
    backHome: 'Back home',
    kicker: 'Tarot reading room',
    title: 'What do you want to ask? Bring it to the table.',
    subtitle: 'Choose a reading atmosphere, write one concern, then sit with a reader. The next step goes straight into card selection.',
    versionSelectorAria: 'Choose reading version',
    versionState: { active: 'Selected', inactive: 'Choose version', badge: 'Try now' },
    versions: {
      healing: {
        name: 'Healing Companion',
        promise: 'For love, relationships, and private worries; the first feeling is being gently held.',
        sampleConcern: 'I feel lost about a relationship and do not know whether to keep going.',
      },
      ritual: {
        name: 'Mystic Ritual',
        promise: 'More immersion, ceremony, and reader authority; like a late-night table reading.',
        sampleConcern: 'I feel like a relationship is unfinished, but I do not know whether to return.',
      },
      clarity: {
        name: 'Rational Clarity',
        promise: 'Turns tarot into a clear decision aid for people who want insight without superstition.',
        sampleConcern: 'I am stuck in an important decision. My mind knows the answer, but my emotions keep pulling me back.',
      },
    },
    concern: { title: 'Give the reader one sentence', placeholder: 'Example: I feel lost about a relationship and do not know whether to keep going.' },
    prompts: [
      { id: 'relationship', tag: 'Love', label: 'Relationship fork', text: 'I feel uncertain about a relationship and do not know whether to move closer or slowly let go.' },
      { id: 'career', tag: 'Career', label: 'Career choice', text: 'I am considering a change in work or collaboration and want to see what I need to understand first.' },
      { id: 'intuition', tag: 'Intuition', label: 'Inner signal', text: 'I keep sensing something I cannot explain and want to know what this feeling is trying to tell me.' },
    ],
    reader: { aria: 'Choose a reader', kicker: 'Reader', title: 'Choose a reader' },
    personas: {
      luna: { name: 'Luna', role: 'Healing companion', blurb: 'Best for love, relationships, and emotional questions; gentle, steady, and emotionally attuned.' },
      orion: { name: 'Orion', role: 'Mystic ritual', blurb: 'Best for fate, major turning points, and questions that need ceremony and weight.' },
      sage: { name: 'Sage', role: 'Rational clarity', blurb: 'Best for career, decisions, and complicated choices; organizes symbols into a clear thread.' },
    },
    auto: { title: 'Let us suggest', default: 'We read your concern and match you with a reader, like drawing a lot.', recommended: 'Recommended {{name}}.' },
    consent: 'I understand this is an AI-assisted tarot service, not medical, legal, or therapy advice.',
    actions: { submitting: 'Opening the table...', startWith: 'Start choosing cards with {{name}}' },
    errors: { api: 'API {{status}} did not complete. Please try again later, or confirm the online backend is deployed.', network: 'The network request did not complete. Please try again later, or confirm the online backend is deployed.' },
    transition: { kicker: 'Transition', title: 'Opening the table' },
  },
  'fr-CA': {
    backHome: 'Retour',
    kicker: 'Salle de tirage tarot',
    title: 'Quelle question voulez-vous poser ? Déposez-la sur la table.',
    subtitle: 'Choisissez une ambiance de lecture, écrivez une préoccupation, puis choisissez une personne lectrice. La prochaine étape mène directement au choix des cartes.',
    versionSelectorAria: 'Choisir la version de lecture',
    versionState: { active: 'Sélectionné', inactive: 'Choisir', badge: 'Essayer' },
    versions: {
      healing: {
        name: 'Accompagnement apaisant',
        promise: 'Pour l’amour, les relations et les confidences; l’entrée donne l’impression d’être doucement accueilli.',
        sampleConcern: 'Je me sens perdu dans une relation et je ne sais pas si je devrais continuer.',
      },
      ritual: {
        name: 'Rituel mystique',
        promise: 'Plus d’immersion, de rituel et d’autorité; comme un tirage de nuit à une vraie table.',
        sampleConcern: 'J’ai l’impression qu’une relation n’est pas terminée, mais je ne sais pas si je devrais revenir.',
      },
      clarity: {
        name: 'Analyse claire',
        promise: 'Transforme le tarot en aide à la décision pour les personnes qui veulent de la clarté sans superstition.',
        sampleConcern: 'Je suis bloqué dans une décision importante. Ma raison connaît la réponse, mais mes émotions me retiennent.',
      },
    },
    concern: { title: 'Donnez une phrase à la personne lectrice', placeholder: 'Exemple : Je me sens perdu dans une relation et je ne sais pas si je devrais continuer.' },
    prompts: [
      { id: 'relationship', tag: 'Relation', label: 'Croisée relationnelle', text: 'Je suis incertain au sujet d’une relation et je ne sais pas si je dois me rapprocher ou lâcher prise doucement.' },
      { id: 'career', tag: 'Carrière', label: 'Choix de carrière', text: 'J’envisage un changement au travail ou dans une collaboration, et je veux voir ce que je dois comprendre d’abord.' },
      { id: 'intuition', tag: 'Intuition', label: 'Signal intérieur', text: 'Je ressens quelque chose que je n’arrive pas à expliquer et je veux savoir ce que cette impression essaie de me dire.' },
    ],
    reader: { aria: 'Choisir une personne lectrice', kicker: 'Reader', title: 'Choisir une personne lectrice' },
    personas: {
      luna: { name: 'Luna', role: 'Accompagnement', blurb: 'Pour l’amour, les relations et les questions sensibles; une présence douce qui accueille d’abord l’émotion.' },
      orion: { name: 'Orion', role: 'Rituel mystique', blurb: 'Pour le destin, les grands tournants et les questions qui demandent du rituel et du poids.' },
      sage: { name: 'Sage', role: 'Analyse claire', blurb: 'Pour la carrière, les choix et les décisions difficiles; transforme les symboles en fil conducteur clair.' },
    },
    auto: { title: 'Nous laisser suggérer', default: 'Nous lisons votre préoccupation et vous associons à une personne lectrice.', recommended: '{{name}} recommandé.' },
    consent: 'Je comprends qu’il s’agit d’un service de tarot assisté par IA, et non d’un avis médical, juridique ou thérapeutique.',
    actions: { submitting: 'Ouverture de la table...', startWith: 'Choisir les cartes avec {{name}}' },
    errors: { api: 'L’API {{status}} n’a pas abouti. Veuillez réessayer plus tard ou vérifier que le backend en ligne est déployé.', network: 'La connexion réseau n’a pas abouti. Veuillez réessayer plus tard ou vérifier que le backend en ligne est déployé.' },
    transition: { kicker: 'Transition', title: 'Ouverture de la table' },
  },
  'es-MX': {
    backHome: 'Volver',
    kicker: 'Sala de lectura tarot',
    title: '¿Qué quieres preguntar? Tráelo a la mesa.',
    subtitle: 'Elige una atmósfera, escribe una inquietud y selecciona una tarotista. El siguiente paso va directo a elegir cartas.',
    versionSelectorAria: 'Elegir versión de lectura',
    versionState: { active: 'Seleccionada', inactive: 'Elegir versión', badge: 'Probar' },
    versions: {
      healing: {
        name: 'Acompañamiento sanador',
        promise: 'Para amor, relaciones y preocupaciones íntimas; desde el inicio se siente como ser recibido con calma.',
        sampleConcern: 'Me siento perdido con una relación y no sé si debería continuar.',
      },
      ritual: {
        name: 'Ritual místico',
        promise: 'Más inmersión, ceremonia y autoridad; como una lectura nocturna en una mesa real.',
        sampleConcern: 'Siento que una relación no ha terminado, pero no sé si debería volver.',
      },
      clarity: {
        name: 'Análisis claro',
        promise: 'Convierte el tarot en una ayuda clara para decidir, sin exigir que la persona sea supersticiosa.',
        sampleConcern: 'Estoy atorado en una decisión importante. Mi mente sabe la respuesta, pero mis emociones me detienen.',
      },
    },
    concern: { title: 'Dale una frase a la tarotista', placeholder: 'Ejemplo: Me siento perdido con una relación y no sé si debería continuar.' },
    prompts: [
      { id: 'relationship', tag: 'Relación', label: 'Cruce afectivo', text: 'Me siento inseguro sobre una relación y no sé si acercarme más o soltar poco a poco.' },
      { id: 'career', tag: 'Carrera', label: 'Decisión laboral', text: 'Estoy considerando un cambio de trabajo o colaboración, y quiero ver qué necesito entender primero.' },
      { id: 'intuition', tag: 'Intuición', label: 'Señal interna', text: 'Siento algo que no puedo explicar y quiero saber qué intenta decirme esa sensación.' },
    ],
    reader: { aria: 'Elegir tarotista', kicker: 'Reader', title: 'Elige una tarotista' },
    personas: {
      luna: { name: 'Luna', role: 'Acompañamiento', blurb: 'Para amor, relaciones y preguntas emocionales; una presencia suave que primero sostiene la emoción.' },
      orion: { name: 'Orion', role: 'Ritual místico', blurb: 'Para destino, cambios importantes y preguntas que necesitan ceremonia y peso.' },
      sage: { name: 'Sage', role: 'Análisis claro', blurb: 'Para carrera, decisiones y elecciones difíciles; ordena los símbolos en una lectura clara.' },
    },
    auto: { title: 'Que recomendemos', default: 'Leemos tu inquietud y te asignamos una tarotista, como sacar una ficha.', recommended: '{{name}} recomendada.' },
    consent: 'Entiendo que es un servicio de tarot asistido por IA, no consejo médico, legal ni terapéutico.',
    actions: { submitting: 'Abriendo la mesa...', startWith: 'Elegir cartas con {{name}}' },
    errors: { api: 'La API {{status}} no se completó. Intenta de nuevo más tarde o confirma que el backend en línea está desplegado.', network: 'La conexión de red no se completó. Intenta de nuevo más tarde o confirma que el backend en línea está desplegado.' },
    transition: { kicker: 'Transition', title: 'Abriendo la mesa' },
  },
};

export const BUSINESS_COPY: Record<UiLanguage, {
  back: string;
  trial: string;
  kicker: string;
  title: string;
  body: string;
  links: Array<{ id: string; label: string; body: string }>;
}> = {
  'zh-TW': {
    back: '回到試玩入口',
    trial: '先試玩',
    kicker: 'Client proposal',
    title: '想把這套流程做成你的線上占卜服務？',
    body: '前面先讓客戶看到終端消費者會玩的完整體驗；這一頁才整理交付內容、合規、代管，以及最後的商模架構。',
    links: [
      { id: 'compliance', label: '法規與多語系', body: 'PIPEDA、Quebec Law 25、英文、加拿大法文、西語與繁中規劃。' },
      { id: 'technical', label: '技術與代管', body: '前台、後台、AI 解讀、資料庫、部署與維運，由我們整理成可交付服務。' },
      { id: 'model', label: '商模架構', body: '免費體驗、付費報告、後續提問，把一對一服務產品化。' },
      { id: 'pricing', label: '收費規劃', body: '建置費、月租代管、終端使用者報告收費與後續擴充。' },
    ],
  },
  en: {
    back: 'Back to trial entry',
    trial: 'Try first',
    kicker: 'Client proposal',
    title: 'Want to turn this flow into your online tarot service?',
    body: 'The trial first shows the full end-customer experience. This page then explains delivery scope, compliance, managed operations, and the business model.',
    links: [
      { id: 'compliance', label: 'Compliance and languages', body: 'PIPEDA, Quebec Law 25, English, Canadian French, Spanish, and Traditional Chinese are planned in.' },
      { id: 'technical', label: 'Technology and hosting', body: 'Frontend, backend, AI reading, database, deployment, and maintenance are packaged as a delivered service.' },
      { id: 'model', label: 'Business model', body: 'Free trial, paid report, and follow-up questions turn one-to-one readings into a scalable product.' },
      { id: 'pricing', label: 'Pricing plan', body: 'Build fee, monthly hosting, end-user report pricing, and future expansion.' },
    ],
  },
  'fr-CA': {
    back: 'Retour à l’essai',
    trial: 'Essayer d’abord',
    kicker: 'Proposition client',
    title: 'Vous voulez transformer ce parcours en service de tarot en ligne ?',
    body: 'L’essai montre d’abord l’expérience complète du client final. Cette page présente ensuite la livraison, la conformité, la gestion technique et le modèle d’affaires.',
    links: [
      { id: 'compliance', label: 'Conformité et langues', body: 'La LPRPDE, la Loi 25 du Québec, l’anglais, le français canadien, l’espagnol et le chinois traditionnel sont prévus.' },
      { id: 'technical', label: 'Technologie et gestion', body: 'Interface, backend, lecture IA, base de données, déploiement et maintenance sont livrés comme un service complet.' },
      { id: 'model', label: 'Modèle d’affaires', body: 'Essai gratuit, rapport payant et questions de suivi transforment les lectures individuelles en produit évolutif.' },
      { id: 'pricing', label: 'Tarification', body: 'Frais de construction, gestion mensuelle, prix des rapports et extensions futures.' },
    ],
  },
  'es-MX': {
    back: 'Volver a la prueba',
    trial: 'Probar primero',
    kicker: 'Propuesta para cliente',
    title: '¿Quieres convertir este flujo en tu servicio de tarot en línea?',
    body: 'La prueba muestra primero la experiencia completa del consumidor final. Esta página resume entrega, cumplimiento, operación administrada y modelo de negocio.',
    links: [
      { id: 'compliance', label: 'Cumplimiento e idiomas', body: 'PIPEDA, Quebec Law 25, inglés, francés canadiense, español y chino tradicional están contemplados.' },
      { id: 'technical', label: 'Tecnología y operación', body: 'Frontend, backend, lectura con IA, base de datos, despliegue y mantenimiento se entregan como servicio.' },
      { id: 'model', label: 'Modelo de negocio', body: 'Prueba gratuita, reporte pagado y preguntas posteriores convierten lecturas uno a uno en un producto escalable.' },
      { id: 'pricing', label: 'Plan de precios', body: 'Costo de construcción, mensualidad de operación, precio de reportes y expansión futura.' },
    ],
  },
};

export const INFO_COPY: Record<UiLanguage, {
  backHome: string;
  complianceLink: string;
  pricingLink: string;
  pages: Record<InfoPageKey, { kicker: string; title: string; body: string[]; cta: string }>;
}> = {
  'zh-TW': {
    backHome: '回首頁',
    complianceLink: '法規 / 多語系',
    pricingLink: '收費規劃',
    pages: {
      compliance: {
        kicker: 'Canada-ready',
        title: '加拿大法規與多語系已納入展示範圍。',
        body: [
          '這個版本會在使用者開始占卜前清楚告知：服務是 AI 輔助，不是真人通靈，也不是心理治療或法律/醫療建議。',
          '資料處理會以 PIPEDA 與 Quebec Law 25 的明確同意、透明揭露、跨境資料處理說明為基準。',
          '多語系規劃包含英文、加拿大法文、西語、繁中；加拿大長居客戶可以直接看到我們有替他的市場做準備。',
        ],
        cta: '回首頁試玩',
      },
      technical: {
        kicker: 'Managed for client',
        title: '客戶不用碰技術後台，我們代管整套服務。',
        body: [
          '網站、AI 對話、塔羅牌圖像、資料庫與報告流程都由我們部署與維護。',
          '客戶不需要理解前端、後端、API key 或資料庫；他只需要選一個版本，拿去展示與收費。',
          '後續如果要換文案、調價格、改占卜師語氣或加語系，都可以在同一套系統上擴充。',
        ],
        cta: '看收費商模',
      },
      pricing: {
        kicker: 'Pricing model',
        title: '一次性建置費，加月租代管，客戶零維運。',
        body: [
          '建議用買斷建置費處理網站、品牌版本、占卜流程、資料合規與部署。',
          '月租代管負責網站維護、AI 成本、資料備份、語系調整、錯誤修復與小幅優化。',
          '終端使用者的單次完整報告可設計成 $20；客戶端收費歸客戶，我們不必做複雜分潤。',
        ],
        cta: '看商業模式',
      },
      model: {
        kicker: 'Business model',
        title: '把資深塔羅師的服務產品化，而不是只做一個漂亮網站。',
        body: [
          '免費段讓使用者輸入心事、選牌、看短解，快速建立投入感。',
          '付費段提供完整書面報告、深度解讀、後續提問，讓使用者明確知道自己在買什麼。',
          '這套模式適合占卜師把原本一對一服務延伸成可規模化的線上入口。',
        ],
        cta: '立即試玩',
      },
    },
  },
  en: {
    backHome: 'Back home',
    complianceLink: 'Compliance / languages',
    pricingLink: 'Pricing plan',
    pages: {
      compliance: {
        kicker: 'Canada-ready',
        title: 'Canadian compliance and multilingual support are built into the demo.',
        body: [
          'Before a reading starts, the service clearly states that it is AI-assisted, not a human psychic, and not medical, legal, or therapy advice.',
          'Data handling is framed around PIPEDA and Quebec Law 25: explicit consent, transparent disclosure, and cross-border processing notices.',
          'The language plan includes English, Canadian French, Spanish, and Traditional Chinese, so a Canada-based client can see the market fit immediately.',
        ],
        cta: 'Try the homepage',
      },
      technical: {
        kicker: 'Managed for client',
        title: 'The client does not need to touch the technical backend.',
        body: [
          'The website, AI conversation, tarot art, database, and report flow are deployed and maintained by us.',
          'The client does not need to understand frontend, backend, API keys, or databases. They choose a version and use it to sell.',
          'Copy, pricing, reader voice, and additional languages can all expand on the same system later.',
        ],
        cta: 'View pricing model',
      },
      pricing: {
        kicker: 'Pricing model',
        title: 'One-time setup plus monthly managed hosting, with no maintenance burden for the client.',
        body: [
          'The setup fee covers the site, branded versions, reading flow, data compliance, and deployment.',
          'The monthly fee covers maintenance, AI cost, backups, language updates, bug fixes, and small improvements.',
          'The complete end-user report can be priced at $20. The client keeps end-customer revenue without a complex revenue-share model.',
        ],
        cta: 'View business model',
      },
      model: {
        kicker: 'Business model',
        title: 'Productize a senior tarot reader’s service, not just a pretty website.',
        body: [
          'The free stage lets users enter a concern, choose cards, and read a short interpretation quickly.',
          'The paid stage offers a complete written report, deeper reading, and follow-up questions, so users know exactly what they are buying.',
          'This model extends one-to-one readings into a scalable online entry point.',
        ],
        cta: 'Try now',
      },
    },
  },
  'fr-CA': {
    backHome: 'Retour',
    complianceLink: 'Conformité / langues',
    pricingLink: 'Tarification',
    pages: {
      compliance: {
        kicker: 'Prêt pour le Canada',
        title: 'La conformité canadienne et le multilingue sont intégrés à la démonstration.',
        body: [
          'Avant le début d’un tirage, le service indique clairement qu’il est assisté par IA, qu’il ne s’agit pas d’une voyance humaine, ni d’un avis médical, juridique ou thérapeutique.',
          'Le traitement des données est cadré par la LPRPDE et la Loi 25 du Québec : consentement manifeste, divulgation transparente et avis sur les transferts hors Québec.',
          'Le plan linguistique inclut l’anglais, le français canadien, l’espagnol et le chinois traditionnel, afin qu’un client établi au Canada voie immédiatement l’adéquation au marché.',
        ],
        cta: 'Essayer l’accueil',
      },
      technical: {
        kicker: 'Géré pour le client',
        title: 'Le client n’a pas à toucher au backend technique.',
        body: [
          'Le site, la conversation IA, les images de tarot, la base de données et le flux de rapport sont déployés et maintenus par nous.',
          'Le client n’a pas besoin de comprendre l’interface, le backend, les clés API ou les bases de données. Il choisit une version et l’utilise pour vendre.',
          'Les textes, les prix, la voix des lecteurs et les langues supplémentaires peuvent tous évoluer sur le même système.',
        ],
        cta: 'Voir la tarification',
      },
      pricing: {
        kicker: 'Modèle tarifaire',
        title: 'Frais de construction uniques, puis gestion mensuelle, sans maintenance côté client.',
        body: [
          'Les frais de construction couvrent le site, les versions de marque, le parcours de tirage, la conformité des données et le déploiement.',
          'La gestion mensuelle couvre la maintenance, les coûts IA, les sauvegardes, les ajustements linguistiques, les corrections et les petites améliorations.',
          'Le rapport complet pour l’utilisateur final peut être vendu 20 $. Le revenu client final reste au client, sans partage complexe.',
        ],
        cta: 'Voir le modèle d’affaires',
      },
      model: {
        kicker: 'Modèle d’affaires',
        title: 'Transformer le service d’une tarologue expérimentée en produit, pas seulement en joli site.',
        body: [
          'La partie gratuite permet d’entrer une préoccupation, de choisir les cartes et de lire une courte interprétation rapidement.',
          'La partie payante offre un rapport écrit complet, une lecture approfondie et des questions de suivi, afin que l’utilisateur sache clairement ce qu’il achète.',
          'Ce modèle étend une pratique individuelle vers une porte d’entrée en ligne évolutive.',
        ],
        cta: 'Essayer maintenant',
      },
    },
  },
  'es-MX': {
    backHome: 'Volver',
    complianceLink: 'Cumplimiento / idiomas',
    pricingLink: 'Plan de precios',
    pages: {
      compliance: {
        kicker: 'Listo para Canadá',
        title: 'El cumplimiento canadiense y el soporte multilingüe están incluidos en la demo.',
        body: [
          'Antes de iniciar una lectura, el servicio aclara que es asistido por IA, no una lectura humana psíquica, ni consejo médico, legal o terapéutico.',
          'El tratamiento de datos se plantea con base en PIPEDA y Quebec Law 25: consentimiento explícito, divulgación transparente y avisos de procesamiento transfronterizo.',
          'El plan de idiomas incluye inglés, francés canadiense, español y chino tradicional, para que un cliente que vive en Canadá vea de inmediato la preparación para su mercado.',
        ],
        cta: 'Probar la portada',
      },
      technical: {
        kicker: 'Administrado para el cliente',
        title: 'El cliente no necesita tocar el backend técnico.',
        body: [
          'El sitio, la conversación con IA, las imágenes de tarot, la base de datos y el flujo de reportes son desplegados y mantenidos por nosotros.',
          'El cliente no necesita entender frontend, backend, API keys o bases de datos. Solo elige una versión y la usa para vender.',
          'Textos, precios, voz de tarotistas e idiomas adicionales pueden crecer sobre el mismo sistema.',
        ],
        cta: 'Ver modelo de precios',
      },
      pricing: {
        kicker: 'Modelo de precios',
        title: 'Costo único de construcción más mensualidad administrada, sin mantenimiento para el cliente.',
        body: [
          'El costo de construcción cubre sitio, versiones de marca, flujo de lectura, cumplimiento de datos y despliegue.',
          'La mensualidad cubre mantenimiento, costos de IA, respaldos, ajustes de idioma, correcciones y mejoras pequeñas.',
          'El reporte completo para usuarios finales puede venderse en $20. Los ingresos finales son del cliente, sin un reparto complejo.',
        ],
        cta: 'Ver modelo de negocio',
      },
      model: {
        kicker: 'Modelo de negocio',
        title: 'Convertir el servicio de una tarotista senior en producto, no solo en un sitio bonito.',
        body: [
          'La etapa gratuita deja que la persona escriba una inquietud, elija cartas y vea una lectura corta rápidamente.',
          'La etapa pagada ofrece un reporte completo, lectura profunda y preguntas de seguimiento, para que la persona sepa exactamente qué compra.',
          'Este modelo extiende lecturas uno a uno hacia una entrada online escalable.',
        ],
        cta: 'Probar ahora',
      },
    },
  },
};

export const DEMO_CTA_COPY: Record<UiLanguage, { kicker: string; title: string; body: string; cta: string }> = {
  'zh-TW': {
    kicker: 'Demo note',
    title: '想把這套流程做成你的線上占卜服務？',
    body: '前面這一輪就是終端消費者會看到的樣子。商業方案、代管、法規與收費架構可以從這裡接著看。',
    cta: '查看客戶方案',
  },
  en: {
    kicker: 'Demo note',
    title: 'Want to turn this flow into your online tarot service?',
    body: 'This round is what the end customer would experience. Business plan, managed operations, compliance, and pricing can continue from here.',
    cta: 'View client plan',
  },
  'fr-CA': {
    kicker: 'Note de démo',
    title: 'Vous voulez transformer ce parcours en service de tarot en ligne ?',
    body: 'Cette ronde montre l’expérience du client final. La proposition, la gestion, la conformité et la tarification continuent ici.',
    cta: 'Voir la proposition',
  },
  'es-MX': {
    kicker: 'Nota demo',
    title: '¿Quieres convertir este flujo en tu servicio de tarot en línea?',
    body: 'Esta ronda muestra lo que verá el consumidor final. La propuesta, operación, cumplimiento y precios continúan aquí.',
    cta: 'Ver plan para cliente',
  },
};
